import React, {useEffect, useState} from "react";
import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Typography,
} from "@mui/material";
import dayjs from "dayjs";
import {deleteStaff, loadStaffWithSchedules, saveStaffWithSchedules, StaffSchedule,} from "../service/DynamoService";
import {StaffInput} from "./StaffInput";
import {DAYS, ScheduleGrid} from "../components/ScheduleGrid";
import {CalendarInput} from "./CalendarInput";
import {verifyCalendar} from "../service/GoogleService";
import {LoadingButton} from "@mui/lab";

// Daily schedule type
interface DailySchedule {
  day: string;
  open: dayjs.Dayjs;
  close: dayjs.Dayjs;
  closed: boolean;
}

// Initialize empty schedule: closed 9–17 for each day
function makeEmptySchedule(): DailySchedule[] {
  return DAYS.map((day) => ({
    day,
    open: dayjs().hour(9).minute(0),
    close: dayjs().hour(17).minute(0),
    closed: true,
  }));
}

export function Staff() {
  const [calendarId, setCalendarId] = useState("");
  const [alert, setAlert] = useState<{ type: 'success' | 'error'; message: string } | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  // State hooks for managing the list and selection
  const [staffList, setStaffList] = useState<string[]>([]);
  const [selectedStaff, setSelectedStaff] = useState("");
  const [staffSchedules, setStaffSchedules] = useState<StaffSchedule[]>([]);

  useEffect(() => {
    loadStaffWithSchedules()
      .then((data) => {
        setStaffSchedules(data);
        const names = data.map((d) => d.name);
        setStaffList(names);
        if (names.length > 0) {
          setSelectedStaff(names[0]);
        }
      })
      .catch(console.error);
  }, []);

  // Schedule state for selected staff
  const [weekly, setWeekly] = useState<DailySchedule[]>(makeEmptySchedule());
  const [isDirty, setIsDirty] = useState(false);

  useEffect(() => {
    const entry = staffSchedules.find((d) => d.name === selectedStaff);
    if (entry) {
      const parsed = entry.schedule.map((row) => ({
        day: row.day,
        open: dayjs(row.open, "HH:mm"),
        close: dayjs(row.close, "HH:mm"),
        closed: row.closed,
      }));
      setCalendarId(entry.calendarId);
      setWeekly(parsed);
    } else {
      setCalendarId("");
      setWeekly(makeEmptySchedule());
    }
  }, [selectedStaff, staffSchedules]);

  useEffect(() => {
    setAlert(null);
    setIsDirty(false);
  }, [selectedStaff]);

  // Add a new staff member if name is non-empty and not already in the list
  const handleAddStaff = async (name: string) => {
    if (name && !staffList.includes(name)) {
      const scheduleStrings = makeEmptySchedule().map((r) => ({
        day: r.day,
        open: r.open.format("HH:mm"),
        close: r.close.format("HH:mm"),
        closed: r.closed,
      }));
      setStaffSchedules((prev) => [
        ...prev,
        {name, calendarId: "", schedule: scheduleStrings},
      ]);
      setStaffList((prev) => [...prev, name]);
      setSelectedStaff(name);
      setCalendarId("");
      try {
        await saveStaffWithSchedules([
          {name, calendarId: "", schedule: scheduleStrings},
        ]);
      } catch (e) {
        console.error(e);
      }
    }
  };

  const [confirmOpen, setConfirmOpen] = useState(false);
  const handleOpenConfirm = () => setConfirmOpen(true);
  const handleCloseConfirm = () => setConfirmOpen(false);
  const handleConfirmDelete = async () => {
    const name = selectedStaff;
    try {
      await deleteStaff(name);
      setStaffSchedules((prev) => prev.filter((d) => d.name !== name));
      setStaffList((prev) => prev.filter((n) => n !== name));
      setSelectedStaff("");
      setWeekly(makeEmptySchedule());
    } catch (e) {
      console.error(e);
    }
    setConfirmOpen(false);
  };

  const handleSave = async () => {
    if (!selectedStaff) return;
    setIsSaving(true); // Start loading

    if (calendarId) {
      const exists = await verifyCalendar(calendarId);
      if (!exists) {
        setAlert({type: 'error', message: 'Die angegebene Kalender-ID existiert nicht.'});
        setIsSaving(false); // Stop loading
        return;
      }
    }

    const scheduleStrings = weekly.map((r) => ({
      day: r.day,
      open: r.open.format("HH:mm"),
      close: r.close.format("HH:mm"),
      closed: r.closed,
    }));
    try {
      await saveStaffWithSchedules([
        {
          name: selectedStaff,
          calendarId: calendarId,
          schedule: scheduleStrings,
        },
      ]);
      setAlert({type: 'success', message: 'Ihre Änderungen wurden gespeichert.'});
      setIsDirty(false);
      setStaffSchedules((prev) =>
        prev.map((d) =>
          d.name === selectedStaff
            ? {name: d.name, calendarId, schedule: scheduleStrings}
            : d,
        ),
      );
    } catch (e) {
      console.error(e);
      setAlert({type: 'error', message: 'Es ist ein Fehler aufgetreten. Bitte versuchen Sie es erneut.'});
    }
    setIsSaving(false); // Stop loading
  };

  // Update a field in the weekly schedule
  const handleWeeklyChange = (
    index: number,
    field: keyof Omit<DailySchedule, "day">,
    value: any,
  ) => {
    // Compute updated weekly and mark dirty
    const updated = weekly.map((row, i) =>
      i === index ? {...row, [field]: value} : row,
    );
    setWeekly(updated);
    // Update local schedules
    setStaffSchedules((prev) =>
      prev.map((d) =>
        d.name === selectedStaff
          ? {
            name: d.name,
            calendarId,
            schedule: updated.map((r) => ({
              day: r.day,
              open: r.open.format("HH:mm"),
              close: r.close.format("HH:mm"),
              closed: r.closed,
            })),
          }
          : d,
      ),
    );
    setIsDirty(true);
  };

  return (
    <Stack spacing={2}>
      <Typography variant="h4" sx={{wordBreak: "break-word"}}>
        Personalverwaltung
      </Typography>

      {/* Input and add button */}
      <StaffInput existingNames={staffList} onAdd={handleAddStaff}/>

      {/* Dropdown and delete button */}
      <Grid
        container
        spacing={{xs: 2}}
        columns={{xs: 3}}
        alignItems="center"
      >
        <Grid size={{xs: 2}}>
          <FormControl variant="outlined" size="small" fullWidth>
            <InputLabel>Mitarbeiter auswählen</InputLabel>
            <Select
              label="Mitarbeiter auswählen"
              value={selectedStaff}
              onChange={(e) => setSelectedStaff(e.target.value as string)}
            >
              {staffList.map((staff) => (
                <MenuItem key={staff} value={staff}>
                  {staff}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
        <Grid size={{xs: 1}}>
          <Button
            onClick={handleOpenConfirm}
            disabled={!selectedStaff}
            variant="outlined"
            color="error"
            fullWidth
          >
            Löschen
          </Button>
        </Grid>
      </Grid>

      <Dialog open={confirmOpen} onClose={handleCloseConfirm}>
        <DialogTitle>Mitarbeiter löschen?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Möchten Sie <em>{selectedStaff}</em> wirklich löschen?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseConfirm}>Abbrechen</Button>
          <Button onClick={handleConfirmDelete} color="error">
            Löschen
          </Button>
        </DialogActions>
      </Dialog>

      {selectedStaff && (
        <>
          <Typography variant="h5">
            Arbeitszeiten von <em>{selectedStaff}</em>
          </Typography>

          {alert && (
            <Alert severity={alert.type} onClose={() => setAlert(null)}>
              {alert.message}
            </Alert>
          )}

          <LoadingButton variant="contained" disabled={!isDirty} onClick={handleSave} loading={isSaving}>
            Änderungen speichern
          </LoadingButton>

          <CalendarInput
            calendarId={calendarId}
            onChange={(id) => {
              setCalendarId(id);
              setIsDirty(true);
            }}
          />

          <ScheduleGrid
            onWeeklyChange={handleWeeklyChange}
            switchLabel="Arbeitet"
            weekly={weekly}
          />
        </>
      )}
    </Stack>
  );
}
